/**
 * Trunnion AI - Chat Header
 * Shows agent name, model, classification, and connection status.
 */

import { Bot, Cpu } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { StatusDot } from '@/components/ui/StatusDot';
import type { ClassificationLevel } from '@/types';

interface ChatHeaderProps {
  agentName: string;
  agentTier?: string;
  modelName?: string;
  classificationLevel?: string;
  isConnected?: boolean;
}

export function ChatHeader({
  agentName,
  agentTier,
  modelName,
  classificationLevel = 'UNCLASSIFIED',
  isConnected = true,
}: ChatHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b border-trunnion-slate-700 bg-trunnion-slate-900 px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-trunnion-gold-500/10">
          <Bot className="h-5 w-5 text-trunnion-gold-500" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-white">{agentName}</h2>
            {agentTier && (
              <Badge variant="default">{agentTier}</Badge>
            )}
          </div>
          {modelName && (
            <div className="flex items-center gap-1.5 text-xs text-trunnion-slate-400">
              <Cpu className="h-3 w-3" />
              <span>{modelName}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <ClassificationTag level={classificationLevel as ClassificationLevel} />
        <div className="flex items-center gap-1.5 text-xs text-trunnion-slate-400">
          <StatusDot status={isConnected ? 'healthy' : 'unhealthy'} />
          <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
        </div>
      </div>
    </div>
  );
}
